import React, { useRef, useState } from 'react';
import { Container, Box, Typography, Paper, Button, Alert, Snackbar } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExtensionIcon from '@mui/icons-material/Extension';
import LaptopIcon from '@mui/icons-material/Laptop';
import Home from './Home';
import useIsMobile from '../hooks/useIsMobile';
import extensionChrome from '../config/extensionChrome.json';
import illuDecompresser from '../assets/extension-chrome/1-decompresser.png';
import illuOuvrirExtensions from '../assets/extension-chrome/2-ouvrir-extensions.png';
import illuModeDeveloppeur from '../assets/extension-chrome/3-mode-developpeur.png';
import illuCharger from '../assets/extension-chrome/4-charger.png';
import illuChoisirDossier from '../assets/extension-chrome/5-choisir-dossier.png';
import illuExtensionInstallee from '../assets/extension-chrome/6-extension-installee.png';
import illuEpingler from '../assets/extension-chrome/7-epingler.png';
import illuMettreAJour from '../assets/extension-chrome/8-mettre-a-jour.png';

// Menu « Extension Chrome » : téléchargement de l'extension VisaMonde (extensionVM) et installation pas à pas, en images.
// Page statique, sans appel réseau. Le zip (public/telechargements/) et ses métadonnées (src/config/extensionChrome.json)
// sont écrits par « npm run paquet », les illustrations (src/assets/extension-chrome/) par « npm run illustrations »,
// tous deux dans extensionVM. Volontairement aucun mode d'emploi de l'extension une fois installée.

const { nom, version, id, fichier, taille, date, chromeMinimum } = extensionChrome;
const URL_PAQUET = `${process.env.PUBLIC_URL}/telechargements/${fichier}`;
// Dossier créé par « Extraire tout… » : le nom du zip sans l'extension (le zip n'a pas de dossier racine)
const DOSSIER = fichier.replace(/\.zip$/i, '');
// Un lien chrome:// ne s'ouvre pas depuis une page web : l'adresse est à copier puis coller dans la barre d'adresse
const ADRESSE_EXTENSIONS = 'chrome://extensions';

const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

// « 2026-10-07 » → « 7 octobre 2026 » (« 1er » le premier du mois) ; une date d'une autre forme est rendue telle quelle
export const dateLisible = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const jour = m ? Number(m[3]) : 0;
  const mois = m ? MOIS[Number(m[2]) - 1] : undefined;
  if (!m || !mois || jour < 1 || jour > 31) return iso;
  return `${jour === 1 ? '1er' : jour} ${mois} ${m[1]}`;
};

// Ko arrondis au supérieur, comme l'Explorateur Windows (même valeur que sur l'illustration 1), puis Mo à une décimale
// à partir de 1 Mo
export const tailleLisible = (octets: number): string => {
  const ko = Math.max(1, Math.ceil(octets / 1024));
  if (ko >= 1024) return `${(octets / (1024 * 1024)).toFixed(1).replace('.', ',')}\u00a0Mo`;
  return `${ko}\u00a0Ko`;
};

// Illustrations : PNG rendus à l'échelle 2, affichés au plus à leur largeur naturelle (jamais agrandis)
interface Illustration {
  src: string;
  alt: string;
  largeur: number;
  hauteur: number;
}

const ILLUSTRATIONS: Record<string, Illustration> = {
  decompresser: {
    src: illuDecompresser, largeur: 760, hauteur: 540,
    alt: `Explorateur Windows : menu du clic droit sur le fichier ${fichier}, avec « Extraire tout… » entouré.`,
  },
  ouvrirExtensions: {
    src: illuOuvrirExtensions, largeur: 768, hauteur: 470,
    alt: 'Chrome : menu principal (trois points verticaux) ouvert sur « Extensions », avec « Gérer les extensions » entouré dans le sous-menu.',
  },
  modeDeveloppeur: {
    src: illuModeDeveloppeur, largeur: 843, hauteur: 60,
    alt: 'Haut de la page des extensions de Chrome : l\'interrupteur « Mode développeur », encore désactivé, est entouré.',
  },
  charger: {
    src: illuCharger, largeur: 720, hauteur: 120,
    alt: 'Page des extensions en mode développeur : le bouton « Charger l\'extension non empaquetée » est entouré.',
  },
  choisirDossier: {
    src: illuChoisirDossier, largeur: 760, hauteur: 506,
    alt: `Fenêtre « Sélectionner le répertoire de l'extension » ouverte sur Documents : le dossier ${DOSSIER} est sélectionné et le bouton « Sélectionner un dossier » est entouré.`,
  },
  extensionInstallee: {
    src: illuExtensionInstallee, largeur: 600, hauteur: 300,
    alt: `Carte de l'extension « ${nom} » dans Chrome, avec l'ID ${id} et l'interrupteur activé.`,
  },
  epingler: {
    src: illuEpingler, largeur: 768, hauteur: 470,
    alt: `Menu Extensions de Chrome (pièce de puzzle) ouvert : la punaise à côté de « ${nom} » est entourée.`,
  },
  mettreAJour: {
    src: illuMettreAJour, largeur: 600, hauteur: 300,
    alt: 'Carte de l\'extension dans Chrome : le bouton « Actualiser » (flèche circulaire), à côté de l\'interrupteur, est entouré.',
  },
};

const ImageIllustration: React.FC<{ illustration: Illustration }> = ({ illustration }) => (
  <Box
    component="img"
    src={illustration.src}
    alt={illustration.alt}
    loading="lazy"
    sx={{
      display: 'block', width: '100%', maxWidth: illustration.largeur, height: 'auto',
      aspectRatio: `${illustration.largeur} / ${illustration.hauteur}`, // place réservée avant le chargement
      mt: 1.5, border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'common.white',
    }}
  />
);

// Libellé d'interface entre guillemets, avec espaces insécables : jamais de « seul en fin de ligne
const Libelle: React.FC<{ children: React.ReactNode }> = ({ children }) => <>«&nbsp;{children}&nbsp;»</>;

// Nom de fichier, de dossier ou ID (police à chasse fixe de la règle « code » de index.css) : passe entier à la ligne,
// coupé seulement s'il est plus large que la ligne
const Code: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    component="code"
    sx={{ display: 'inline-block', maxWidth: '100%', fontSize: '0.9em', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, overflowWrap: 'anywhere' }}
  >
    {children}
  </Box>
);

const Texte: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="body2" sx={{ mt: 0.5, lineHeight: 1.6 }}>{children}</Typography>
);

// Une étape : pastille numérotée (le numéro est aussi porté par la liste ordonnée pour les lecteurs d'écran), titre,
// texte court et illustration(s)
const Etape: React.FC<{ numero: number; titre: string; children: React.ReactNode }> = ({ numero, titre, children }) => (
  <Box component="li" sx={{ display: 'flex', gap: { xs: 1.5, sm: 2 }, py: 2.5, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
    <Box
      aria-hidden
      sx={{
        flex: '0 0 auto', width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main', color: 'primary.contrastText',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold',
      }}
    >
      {numero}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {/* Ligne de 24 px + 4 px au-dessus : la première ligne du titre est centrée sur la pastille de 32 px */}
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 'bold', lineHeight: 1.5, pt: 0.5 }}>{titre}</Typography>
      {children}
    </Box>
  </Box>
);

const Section: React.FC<{ idTitre: string; titre: string; children: React.ReactNode }> = ({ idTitre, titre, children }) => (
  <Box component="section" aria-labelledby={idTitre} sx={{ mt: 4 }}>
    <Typography id={idTitre} variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>{titre}</Typography>
    {children}
  </Box>
);

const ExtensionChromeScreen: React.FC = () => {
  const isMobile = useIsMobile();
  const adresseRef = useRef<HTMLElement>(null);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'info' } | null>(null);

  // Repli sans presse-papiers (page en http, permission refusée) : l'adresse est sélectionnée, prête pour Ctrl+C
  const selectionnerAdresse = () => {
    const el = adresseRef.current;
    const selection = window.getSelection();
    if (!el || !selection) return;
    const range = document.createRange();
    range.selectNodeContents(el);
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const copierAdresse = async () => {
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(ADRESSE_EXTENSIONS);
        setToast({ msg: 'Adresse copiée : collez-la dans la barre d\'adresse de Chrome (Ctrl+V), puis appuyez sur Entrée.', sev: 'success' });
        return;
      }
    } catch (e) {
      console.error('Presse-papiers indisponible', e);
    }
    selectionnerAdresse();
    setToast({ msg: 'Copie automatique impossible : l\'adresse est sélectionnée, copiez-la avec Ctrl+C (Cmd+C sur Mac).', sev: 'info' });
  };

  return (
    <>
      <Home />
      <Container maxWidth="md" sx={{ mt: 2, mb: 6, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>Extension Chrome</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Assistant VisaMonde qui remplit les formulaires eVisa officiels (Inde) à partir des dossiers. À installer une fois sur
          chaque ordinateur, dans Google Chrome.
        </Typography>

        {isMobile && (
          <Alert severity="info" icon={<LaptopIcon />} sx={{ mt: 2 }}>
            L'installation se fait sur un ordinateur&nbsp;: ouvrez cette page dans Google Chrome, sur l'ordinateur à équiper.
          </Alert>
        )}

        <Paper
          component="section"
          aria-label="Téléchargement"
          variant="outlined"
          sx={{ mt: 3, p: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          <ExtensionIcon color="primary" sx={{ fontSize: 40 }} />
          <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
            <Typography variant="subtitle1" component="p" sx={{ fontWeight: 'bold' }}>{nom}</Typography>
            <Typography variant="body2" color="text.secondary">
              Version {version} · {dateLisible(date)} · {tailleLisible(taille)}
            </Typography>
          </Box>
          <Button variant="contained" size="large" component="a" href={URL_PAQUET} download={fichier} startIcon={<DownloadIcon />}>
            Télécharger l'extension
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ flexBasis: '100%' }}>
            Google Chrome {chromeMinimum} ou plus récent, sur ordinateur (Windows ou Mac).
          </Typography>
        </Paper>

        <Section idTitre="extension-installation" titre="Installation">
          <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
            <Etape numero={1} titre="Décompresser le fichier">
              <Texte>
                Rangez le fichier téléchargé dans un dossier où il restera, par exemple Documents. Faites ensuite un clic droit
                dessus → <Libelle>Extraire tout…</Libelle> → <Libelle>Extraire</Libelle>, sans changer la destination
                proposée&nbsp;: Windows crée à côté le dossier <Code>{DOSSIER}</Code>. Sur Mac&nbsp;: double-cliquez sur le
                fichier.
              </Texte>
              <Texte>
                <strong>Chrome lit l'extension dans ce dossier&nbsp;: ne le supprimez pas et ne le déplacez pas.</strong>
              </Texte>
              <ImageIllustration illustration={ILLUSTRATIONS.decompresser} />
            </Etape>

            <Etape numero={2} titre="Ouvrir la page des extensions de Chrome">
              <Texte>
                Dans Chrome&nbsp;: menu ⋮ (en haut à droite) → <Libelle>Extensions</Libelle> → <Libelle>Gérer les
                extensions</Libelle>. Ou copiez l'adresse ci-dessous, collez-la dans la barre d'adresse et appuyez sur Entrée
                (Chrome n'ouvre pas cette page depuis un lien).
              </Texte>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                <Box
                  component="code"
                  ref={adresseRef}
                  sx={{ fontSize: '0.95rem', bgcolor: 'grey.100', border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.5, userSelect: 'all' }}
                >
                  {ADRESSE_EXTENSIONS}
                </Box>
                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={copierAdresse}>
                  Copier l'adresse
                </Button>
              </Box>
              <ImageIllustration illustration={ILLUSTRATIONS.ouvrirExtensions} />
            </Etape>

            <Etape numero={3} titre="Activer le mode développeur">
              <Texte>
                En haut à droite de la page des extensions, activez l'interrupteur <Libelle>Mode développeur</Libelle>.
                Laissez-le activé.
              </Texte>
              <ImageIllustration illustration={ILLUSTRATIONS.modeDeveloppeur} />
            </Etape>

            <Etape numero={4} titre="Charger l'extension">
              <Texte>
                Cliquez sur <Libelle>Charger l'extension non empaquetée</Libelle>. Dans la fenêtre <Libelle>Sélectionner le
                répertoire de l'extension</Libelle>, ouvrez le dossier où vous avez rangé le fichier (par exemple Documents),
                sélectionnez le dossier <Code>{DOSSIER}</Code>, celui qui contient <Code>manifest.json</Code>, puis cliquez
                sur <Libelle>Sélectionner un dossier</Libelle>.
              </Texte>
              <ImageIllustration illustration={ILLUSTRATIONS.charger} />
              <ImageIllustration illustration={ILLUSTRATIONS.choisirDossier} />
            </Etape>

            <Etape numero={5} titre="Vérifier">
              <Texte>
                La carte <Libelle>{nom}</Libelle> apparaît, avec son interrupteur activé. Son ID est <Code>{id}</Code>.
              </Texte>
              <ImageIllustration illustration={ILLUSTRATIONS.extensionInstallee} />
            </Etape>

            <Etape numero={6} titre="Épingler l'icône">
              <Texte>
                Cliquez sur l'icône Extensions (pièce de puzzle), à droite de la barre d'adresse, puis sur la punaise à côté
                de <Libelle>{nom}</Libelle>. L'icône verte V reste alors visible dans la barre d'outils.
              </Texte>
              <ImageIllustration illustration={ILLUSTRATIONS.epingler} />
            </Etape>
          </Box>
          <Alert severity="success" sx={{ mt: 1 }}>
            C'est prêt&nbsp;: sur le portail eVisa indien, cliquez sur l'icône VisaMonde pour ouvrir l'assistant.
          </Alert>
        </Section>

        <Section idTitre="extension-mise-a-jour" titre="Mettre à jour">
          <Texte>
            Quand une nouvelle version est publiée ici (comparez son numéro, en haut de cette page, avec celui de la carte de
            l'extension)&nbsp;:
          </Texte>
          <Box component="ol" sx={{ m: 0, mt: 1, pl: 3, typography: 'body2', lineHeight: 1.6, '& li + li': { mt: 0.5 } }}>
            <li>Téléchargez le nouveau fichier avec le bouton <Libelle>Télécharger l'extension</Libelle>.</li>
            <li>
              Clic droit sur le fichier → <Libelle>Extraire tout…</Libelle> → <Libelle>Parcourir…</Libelle>&nbsp;: choisissez
              le dossier de l'installation, celui qui contient <Code>manifest.json</Code> (il garde le nom de la version
              installée la première fois). Cliquez sur <Libelle>Extraire</Libelle>, puis sur <Libelle>Remplacer les fichiers
              dans la destination</Libelle>. Sur Mac&nbsp;: double-cliquez sur le fichier, puis remplacez le contenu du dossier
              de l'installation par celui du nouveau dossier.
            </li>
            <li>
              Sur la page <Code>{ADRESSE_EXTENSIONS}</Code>, cliquez sur ↻ (<Libelle>Actualiser</Libelle>) dans la carte de
              l'extension&nbsp;: elle affiche le nouveau numéro de version.
            </li>
            <li>Rechargez l'onglet du portail (F5).</li>
          </Box>
          <ImageIllustration illustration={ILLUSTRATIONS.mettreAJour} />
          <Alert severity="warning" sx={{ mt: 2 }}>
            Ne supprimez pas l'extension pour la mettre à jour&nbsp;: les informations complétées dans les fiches seraient
            perdues (les réglages de l'extension permettent de les exporter).
          </Alert>
        </Section>

        <Section idTitre="extension-problemes" titre="En cas de problème">
          <Box component="ul" sx={{ m: 0, pl: 3, typography: 'body2', lineHeight: 1.6, '& li + li': { mt: 0.5 } }}>
            <li>
              <strong><Libelle>Fichier manifeste absent ou illisible</Libelle></strong>&nbsp;: le dossier choisi n'est pas le
              bon. Choisissez celui qui contient directement <Code>manifest.json</Code>&nbsp;: ni le fichier .zip, ni le
              dossier au-dessus.
            </li>
            <li>
              <strong>Interrupteur <Libelle>Mode développeur</Libelle> grisé</strong> (<Libelle>Ce paramètre est géré par
              votre administrateur</Libelle>)&nbsp;: ce Chrome est géré par une organisation, l'extension ne peut pas y être
              installée.
            </li>
            <li>
              <strong>L'icône VisaMonde n'apparaît pas</strong> dans la barre d'outils&nbsp;: épinglez-la (étape 6).
            </li>
          </Box>
        </Section>
      </Container>

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default ExtensionChromeScreen;
